import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LISTADOS } from './definiciones';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import { getCarpetasJauregui, getBloquesJauregui } from '../../services/jaureguiService';
import { getTipoTramites } from '../../services/tipoTramiteService';
import { usuarioService } from '../../services/usuarioService';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import PageHeader from '../../components/ui/PageHeader';

const DEF = LISTADOS.jauregui;

/**
 * En qué bloque cae cada carpeta: las 7 secciones de la pestaña PARA CARGAR JAUREGUI.
 *
 * El backend ya devuelve el bloque resuelto (la corrección a mano gana sobre la deducción) y su
 * etiqueta, así que acá solo se agrupa — mismo criterio que "Para aprobar" con sus secciones.
 */
function bloquePorFila(row) {
    return {
        clave: row.bloque || 'SIN_BLOQUE',
        etiqueta: row.bloqueEtiqueta || 'Sin clasificar',
        // "Carpetas viejas perdidas" nace cerrado: son carpetas que ya no se trabajan y ocupaban la
        // pantalla arriba del trabajo real. El flag lo trae el backend (BloqueJauregui.sinPlazo),
        // que es el mismo que le saca el semáforo, así que las dos cosas no pueden desalinearse.
        // TablaListado lo aplica una sola vez: si el usuario lo abre, queda abierto.
        colapsadoInicial: row.bloqueSinPlazo === true,
    };
}

/**
 * Primer tramo del circuito del estudio: **para cargar Jauregui** → a liquidar → turno ANSES →
 * para aprobar → cobranzas. Son las carpetas que alguien tiene para cargar el cálculo, antes de
 * que exista un reclamo.
 *
 * Reemplaza a la pestaña PARA CARGAR JAUREGUI de la planilla (139 filas, 7 bloques) y responde
 * tres preguntas: quién tiene cada carpeta, cuáles están demoradas y a quién hay que llamar.
 *
 * ES SOLO LECTURA A PROPÓSITO
 * Todo se edita en la tarjeta "Carga en Jauregui" de la ficha del trámite: el listado es la vista
 * de coordinación, y tener los dos lados editables obligaba a duplicar el formulario (con sus dos
 * plazos y su selector de bloque) en una celda de tabla. La fila lleva a la ficha.
 *
 * Los bloques se piden al backend en vez de escribirlos acá, porque la deducción vive allá
 * (ListadoJaureguiService) y las dos cosas tienen que moverse juntas.
 */
export default function ListadoCargarJauregui() {
    const navigate = useNavigate();
    const [bloque, setBloque] = useFiltroPersistente('listadoJauregui.bloque', '');
    const [usuario, setUsuario] = useFiltroPersistente('listadoJauregui.usuario', '');
    const [estadoPlazo, setEstadoPlazo] = useFiltroPersistente('listadoJauregui.estadoPlazo', '');
    const [tipo, setTipo] = useFiltroPersistente('listadoJauregui.tipo', '');

    const [bloques, setBloques] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [tipos, setTipos] = useState([]);

    useEffect(() => {
        getBloquesJauregui().then(setBloques).catch(() => setBloques([]));
        getTipoTramites()
            .then((datos) => setTipos(Array.isArray(datos) ? datos : []))
            .catch(() => setTipos([]));
        // getParaSeleccion y no getAll: es la lista mínima para selectores operativos, visible para
        // cualquier rol autenticado (getAll exige el permiso de administrar usuarios).
        usuarioService.getParaSeleccion()
            .then((resp) => {
                const data = resp?.data;
                setUsuarios(Array.isArray(data) ? data : (data?.content || []));
            })
            .catch(() => setUsuarios([]));
    }, []);

    // useCallback y no una flecha inline: TablaListado la usa como dependencia de su useEffect, y
    // una función nueva en cada render dispara un pedido por render.
    const fetchPage = useCallback(
        (page, size, texto) => getCarpetasJauregui(
            { bloque, idUsuarioCarga: usuario, estadoPlazo, idTipoTramite: tipo }, page, size, texto),
        [bloque, usuario, estadoPlazo, tipo]
    );

    const etiquetaBloque = (valor) => bloques.find((b) => b.valor === valor)?.etiqueta || 'Bloque';
    const etiquetaUsuario = (valor) => {
        const u = usuarios.find((x) => String(x.id) === String(valor));
        return u ? (u.nombre || u.email) : 'Quién carga';
    };

    const chipsExtra = [
        bloque && { key: 'bloque', label: etiquetaBloque(bloque), onQuitar: () => setBloque('') },
        usuario && { key: 'usuario', label: etiquetaUsuario(usuario), onQuitar: () => setUsuario('') },
        estadoPlazo && {
            key: 'plazo',
            label: estadoPlazo === 'DEMORADO' ? 'Solo demoradas' : 'Solo en plazo',
            onQuitar: () => setEstadoPlazo(''),
        },
        tipo && {
            key: 'tipo',
            label: tipos.find((t) => String(t.idTipoTramite) === String(tipo))?.nombre || 'Tipo',
            onQuitar: () => setTipo(''),
        },
    ].filter(Boolean);

    const limpiarExtra = () => {
        setBloque('');
        setUsuario('');
        setEstadoPlazo('');
        setTipo('');
    };

    return (
        <div className="container-fluid mt-4 listado-pagina area-cobranzas">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            <TablaListado
                claveFiltro="listadoJauregui"
                columnas={DEF.columnas}
                fetchPage={fetchPage}
                onRowClick={(row) => navigate(rutaDetalleTramite(row))}
                rowKey={(row) => row.idTramite}
                rowClassName={DEF.rowClassName}
                dependencias={[bloque, usuario, estadoPlazo, tipo]}
                agruparPor={bloquePorFila}
                chipsExtra={chipsExtra}
                onLimpiarExtra={limpiarExtra}
                filtrosExtra={
                    <>
                        <div>
                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem', color: '#6B7280' }}>
                                <i className="bi bi-collection me-2"></i>Bloque
                            </label>
                            <select className="form-select" value={bloque} onChange={(e) => setBloque(e.target.value)}>
                                <option value="">Todos</option>
                                {bloques.map((b) => (
                                    <option key={b.valor} value={b.valor}>{b.etiqueta}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem', color: '#6B7280' }}>
                                <i className="bi bi-person-check me-2"></i>¿Quién carga?
                            </label>
                            <select className="form-select" value={usuario} onChange={(e) => setUsuario(e.target.value)}>
                                <option value="">Todos</option>
                                {usuarios.map((u) => (
                                    <option key={u.id} value={u.id}>{u.nombre || u.email}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem', color: '#6B7280' }}>
                                <i className="bi bi-stopwatch me-2"></i>Plazo
                            </label>
                            <select className="form-select" value={estadoPlazo} onChange={(e) => setEstadoPlazo(e.target.value)}>
                                <option value="">Todas</option>
                                <option value="DEMORADO">Solo demoradas</option>
                                <option value="EN_PLAZO">Solo en plazo</option>
                            </select>
                        </div>
                        <div>
                            <label className="form-label" style={{ fontWeight: 500, fontSize: '0.9rem', color: '#6B7280' }}>
                                <i className="bi bi-folder2-open me-2"></i>Tipo de trámite
                            </label>
                            <select className="form-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                                <option value="">Todos</option>
                                {tipos.map((t) => (
                                    <option key={t.idTipoTramite} value={t.idTipoTramite}>{t.nombre}</option>
                                ))}
                            </select>
                        </div>
                    </>
                }
            />
        </div>
    );
}
